import React, { useState, useEffect } from 'react';
import { Search, Database, Package, ShoppingCart, Truck, BarChart3, MapPin, RefreshCw, Eye, List, Grid } from 'lucide-react';
import { dataInspectionService } from '../../../../services/dataInspectionService';
import { DataInspectionSummaryDto } from '../../../../types/dataInspection';
import { PListViewer } from './viewers/PListViewer';
import { OrdersViewer } from './viewers/OrdersViewer';
import { POrdersViewer } from './viewers/POrdersViewer';
import { StcTransOrdersViewer } from './viewers/StcTransOrdersViewer';
import { ItemBinsViewer } from './viewers/ItemBinsViewer';
import { StcBinsViewer } from './viewers/StcBinsViewer';

type DataType = 'summary' | 'plists' | 'orders' | 'porders' | 'stctrans' | 'itembins' | 'stcbins';

export const DataInspection: React.FC = () => {
    const [currentView, setCurrentView] = useState<DataType>('summary');
    const [summary, setSummary] = useState<DataInspectionSummaryDto | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string>('');

    const dataTypes = [
        { id: 'plists', name: 'Plukklister', icon: List, description: 'Vis plukklister og tilhørende linjer' },
        { id: 'orders', name: 'Salgsordrer', icon: ShoppingCart, description: 'Vis salgsordrer og orderlinjer' },
        { id: 'porders', name: 'Innkjøpsordrer', icon: Truck, description: 'Vis innkjøpsordrer og orderlinjer' },
        { id: 'stctrans', name: 'Lageroverføringer', icon: Package, description: 'Vis lageroverføringer og linjer' },
        { id: 'itembins', name: 'Lagersaldo', icon: BarChart3, description: 'Vis lagersaldo og varebeholdning' },
        { id: 'stcbins', name: 'Lokasjoner', icon: MapPin, description: 'Vis lagerlokasjoner og oppsettet' }
    ];

    useEffect(() => {
        loadSummary();
    }, []);

    const loadSummary = async () => {
        try {
            setLoading(true);
            setError('');
            const summaryData = await dataInspectionService.getDataSummary();
            setSummary(summaryData);
        } catch (err) {
            setError(err instanceof Error ? err.message : 'En feil oppstod');
        } finally {
            setLoading(false);
        }
    };

    const handleViewChange = (viewType: DataType) => {
        setCurrentView(viewType);
    };

    const handleBackToSummary = () => {
        setCurrentView('summary');
    };

    const renderViewer = () => {
        switch (currentView) {
            case 'plists':
                return <PListViewer onBack={handleBackToSummary} />;
            case 'orders':
                return <OrdersViewer onBack={handleBackToSummary} />;
            case 'porders':
                return <POrdersViewer onBack={handleBackToSummary} />;
            case 'stctrans':
                return <StcTransOrdersViewer onBack={handleBackToSummary} />;
            case 'itembins':
                return <ItemBinsViewer onBack={handleBackToSummary} />;
            case 'stcbins':
                return <StcBinsViewer onBack={handleBackToSummary} />;
            default:
                return null;
        }
    };

    if (currentView !== 'summary') {
        return renderViewer();
    }

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="bg-gradient-to-r from-blue-600 to-purple-600 text-white p-6 rounded-lg">
                <div className="flex items-center gap-3 mb-2">
                    <Search className="w-8 h-8" />
                    <h1 className="text-2xl font-bold">Grunndata-inspeksjon</h1>
                </div>
                <p className="text-blue-100">Inspiser og analyser systemets grunndata</p>
                <div className="flex items-center gap-2 mt-4">
                    <button
                        onClick={loadSummary}
                        disabled={loading}
                        className="flex items-center gap-2 px-4 py-2 bg-white bg-opacity-20 hover:bg-opacity-30 rounded-lg transition-colors disabled:opacity-50"
                    >
                        <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
                        Oppdater sammendrag
                    </button>
                </div>
            </div>

            {/* Error Message */}
            {error && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                    <div className="flex items-center gap-2 text-red-700">
                        <div className="w-2 h-2 bg-red-400 rounded-full"></div>
                        <span className="font-medium">Feil ved lasting av data:</span>
                    </div>
                    <p className="text-red-600 mt-1">{error}</p>
                </div>
            )}

            {/* Summary Cards */}
            {summary && (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    <div className="bg-white rounded-lg p-6 shadow-sm border border-gray-200">
                        <div className="flex items-center gap-3 mb-3">
                            <List className="w-6 h-6 text-blue-600" />
                            <h3 className="font-semibold text-gray-900">Plukklister</h3>
                        </div>
                        <div className="space-y-2">
                            <div className="flex justify-between">
                                <span className="text-gray-600">Totalt:</span>
                                <span className="font-semibold">{summary.pLists.total}</span>
                            </div>
                            <div className="flex justify-between">
                                <span className="text-gray-600">Aktive:</span>
                                <span className="font-semibold text-green-600">{summary.pLists.active}</span>
                            </div>
                            <div className="flex justify-between">
                                <span className="text-gray-600">Ferdigstilt:</span>
                                <span className="font-semibold text-blue-600">{summary.pLists.completed}</span>
                            </div>
                        </div>
                    </div>

                    <div className="bg-white rounded-lg p-6 shadow-sm border border-gray-200">
                        <div className="flex items-center gap-3 mb-3">
                            <ShoppingCart className="w-6 h-6 text-green-600" />
                            <h3 className="font-semibold text-gray-900">Salgsordrer</h3>
                        </div>
                        <div className="space-y-2">
                            <div className="flex justify-between">
                                <span className="text-gray-600">Totalt:</span>
                                <span className="font-semibold">{summary.orders.total}</span>
                            </div>
                            <div className="flex justify-between">
                                <span className="text-gray-600">Ventende:</span>
                                <span className="font-semibold text-yellow-600">{summary.orders.pending}</span>
                            </div>
                            <div className="flex justify-between">
                                <span className="text-gray-600">Ferdigstilt:</span>
                                <span className="font-semibold text-blue-600">{summary.orders.completed}</span>
                            </div>
                        </div>
                    </div>

                    <div className="bg-white rounded-lg p-6 shadow-sm border border-gray-200">
                        <div className="flex items-center gap-3 mb-3">
                            <Truck className="w-6 h-6 text-purple-600" />
                            <h3 className="font-semibold text-gray-900">Innkjøpsordrer</h3>
                        </div>
                        <div className="space-y-2">
                            <div className="flex justify-between">
                                <span className="text-gray-600">Totalt:</span>
                                <span className="font-semibold">{summary.pOrders.total}</span>
                            </div>
                            <div className="flex justify-between">
                                <span className="text-gray-600">Ventende:</span>
                                <span className="font-semibold text-yellow-600">{summary.pOrders.pending}</span>
                            </div>
                            <div className="flex justify-between">
                                <span className="text-gray-600">Ferdigstilt:</span>
                                <span className="font-semibold text-blue-600">{summary.pOrders.completed}</span>
                            </div>
                        </div>
                    </div>

                    <div className="bg-white rounded-lg p-6 shadow-sm border border-gray-200">
                        <div className="flex items-center gap-3 mb-3">
                            <Package className="w-6 h-6 text-orange-600" />
                            <h3 className="font-semibold text-gray-900">Lageroverføringer</h3>
                        </div>
                        <div className="space-y-2">
                            <div className="flex justify-between">
                                <span className="text-gray-600">Totalt:</span>
                                <span className="font-semibold">{summary.stcTransOrders.total}</span>
                            </div>
                            <div className="flex justify-between">
                                <span className="text-gray-600">Ventende:</span>
                                <span className="font-semibold text-yellow-600">{summary.stcTransOrders.pending}</span>
                            </div>
                            <div className="flex justify-between">
                                <span className="text-gray-600">Ferdigstilt:</span>
                                <span className="font-semibold text-blue-600">{summary.stcTransOrders.completed}</span>
                            </div>
                        </div>
                    </div>

                    <div className="bg-white rounded-lg p-6 shadow-sm border border-gray-200">
                        <div className="flex items-center gap-3 mb-3">
                            <BarChart3 className="w-6 h-6 text-teal-600" />
                            <h3 className="font-semibold text-gray-900">Lagersaldo</h3>
                        </div>
                        <div className="space-y-2">
                            <div className="flex justify-between">
                                <span className="text-gray-600">Totalt:</span>
                                <span className="font-semibold">{summary.itemBins.total}</span>
                            </div>
                            <div className="flex justify-between">
                                <span className="text-gray-600">Lokasjoner:</span>
                                <span className="font-semibold text-green-600">{summary.itemBins.locations}</span>
                            </div>
                            <div className="flex justify-between">
                                <span className="text-gray-600">Total mengde:</span>
                                <span className="font-semibold text-blue-600">{summary.itemBins.totalQuantity}</span>
                            </div>
                        </div>
                    </div>

                    <div className="bg-white rounded-lg p-6 shadow-sm border border-gray-200">
                        <div className="flex items-center gap-3 mb-3">
                            <MapPin className="w-6 h-6 text-red-600" />
                            <h3 className="font-semibold text-gray-900">Lokasjoner</h3>
                        </div>
                        <div className="space-y-2">
                            <div className="flex justify-between">
                                <span className="text-gray-600">Totalt:</span>
                                <span className="font-semibold">{summary.stcBins.total}</span>
                            </div>
                            <div className="flex justify-between">
                                <span className="text-gray-600">Aktive:</span>
                                <span className="font-semibold text-green-600">{summary.stcBins.active}</span>
                            </div>
                            <div className="flex justify-between">
                                <span className="text-gray-600">Blokkerte:</span>
                                <span className="font-semibold text-red-600">{summary.stcBins.blocked}</span>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Data Type Grid */}
            <div className="bg-white rounded-lg p-6 shadow-sm border border-gray-200">
                <h2 className="text-lg font-semibold text-gray-900 mb-4">Inspiser grunndata</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {dataTypes.map(dataType => {
                        const Icon = dataType.icon;
                        return (
                            <button
                                key={dataType.id}
                                onClick={() => handleViewChange(dataType.id as DataType)}
                                className="flex items-start gap-3 p-4 text-left border border-gray-200 rounded-lg hover:border-blue-300 hover:bg-blue-50 transition-colors group"
                            >
                                <div className="bg-gray-100 p-2 rounded-lg group-hover:bg-blue-100 transition-colors">
                                    <Icon className="w-5 h-5 text-gray-600 group-hover:text-blue-600" />
                                </div>
                                <div className="flex-1">
                                    <h3 className="font-medium text-gray-900 mb-1">{dataType.name}</h3>
                                    <p className="text-sm text-gray-600">{dataType.description}</p>
                                </div>
                                <Eye className="w-4 h-4 text-gray-400 opacity-0 group-hover:opacity-100 transition-opacity" />
                            </button>
                        );
                    })}
                </div>
            </div>
        </div>
    );
};
